import { useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { RecipeView } from "../components/RecipeView";
import { ExampleList, KitchenPending, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { alternateRequest, buildPreferences, kitchenRequest, type PreferencesForm } from "../lib/kitchen";

const EXAMPLES = [
  "I have chicken thighs, a bag of spinach, half an onion, rice, feta and a couple of lemons",
  "Eggs, tortillas, a can of black beans, salsa, cheddar and one avocado. 15 minutes max.",
  "Leftover roast chicken, carrots, celery, a box of penne and parmesan",
];

export function PantryRecipePage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [prefs, setPrefs] = useState<PreferencesForm>({ servings: 2 });
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const [pendingAlt, setPendingAlt] = useState<string | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.recipes.fromPantry(req), onSettled: () => setPendingAlt(null) });

  const send = (req: KitchenToolRequest) => {
    setLast(req);
    m.mutate(req);
  };
  const submit = (text = input.text) => {
    if (!text.trim() && !input.media) return;
    setInput({ ...input, text });
    send(kitchenRequest({ text, media: input.media, message: composeMessage(note), preferences: buildPreferences(prefs) }));
  };
  const makeAlternate = (title: string) => {
    if (!last) return;
    setPendingAlt(title);
    send(alternateRequest(last, title, m.data?.data.detectedIngredients));
  };
  const data = m.data?.data;
  const firstLoad = m.isPending && !pendingAlt;

  return (
    <RequireFeature feature="cooking">
      <PageHeader
        eyebrow="POST /napi/tools/recipes/fromPantry"
        title="What can I cook?"
        description="Show the fridge, pantry or counter (photo, short video or voice note) or type what you have. You get one full recipe with amounts, heat, timings and doneness cues, ingredients linked to Passio foods, and a few other meal ideas to switch to."
        actions={<RouteModelPicker routes={["recipesFromPantry"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <MediaInput value={input} onChange={setInput} textPlaceholder="List what you have, or describe the fridge…" photoLabel="Drop a photo of your fridge, pantry or counter" />
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["likes", "dislikes", "diet", "allergies", "servings", "maxMinutes", "cuisine", "skill", "equipment"]} defaultOpen />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. something the kids will eat" />
              <Button leftSection={<IconSparkles size={16} />} onClick={() => submit()} loading={firstLoad} disabled={m.isPending || (!input.text.trim() && !input.media)} size="md">
                What can I cook?
              </Button>
              {!input.media && <ExampleList examples={EXAMPLES} onPick={submit} />}
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && send(last)} />}
          {firstLoad && <KitchenPending title="Checking what you have and writing a recipe…" />}
          {data && !firstLoad && (
            <Stack gap="md">
              <ResultHeader title={last?.selectedMeal ? "Your pick" : "Tonight's recipe"} meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              {data.detectedIngredients.length > 0 && (
                <Card>
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: "0.05em" }}>
                    What you have ({data.detectedIngredients.length})
                  </Text>
                  <Group gap={6}>
                    {data.detectedIngredients.map((d, i) => (
                      <Tooltip key={i} label={`${d.source}${d.quantity ? ` · ${d.quantity}` : ""} · ${Math.round(d.confidence * 100)}% sure`}>
                        <Badge variant={data.unusedIngredients.includes(d.name) ? "outline" : "light"} color={d.expiringSoon ? "orange" : d.confidence < 0.6 ? "gray" : "violet"} tt="none">
                          {d.name}
                          {d.expiringSoon ? " · use soon" : ""}
                          {d.confidence < 0.6 ? " ?" : ""}
                        </Badge>
                      </Tooltip>
                    ))}
                  </Group>
                  {data.unusedIngredients.length > 0 && (
                    <Text fz="xs" c="dimmed" mt={6}>
                      Not used this time: {data.unusedIngredients.join(", ")}
                    </Text>
                  )}
                </Card>
              )}
              {data.notes && (
                <Alert color="violet" variant="light">
                  <Text fz="sm">{data.notes}</Text>
                </Alert>
              )}
              {data.recipe ? (
                <RecipeView recipe={data.recipe} missing={data.missingIngredients} alternates={data.alternateMeals} alternatesTitle="Or make one of these instead" onAlternate={makeAlternate} pendingAlternate={pendingAlt} />
              ) : (
                <EmptyState title="No recipe this time">{data.notes || "Nothing usable came through. Try a brighter photo, or list a few ingredients."}</EmptyState>
              )}
            </Stack>
          )}
          {!data && !m.isPending && !m.error && (
            <EmptyState title="Show us your kitchen">A fridge photo works best taken straight on with the door wide open. Voice and video go to Gemini models.</EmptyState>
          )}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
